import get from 'lodash/get';
import {useState} from 'react';

import 'react-credit-cards/es/styles-compiled.css';
import Cards from 'react-credit-cards';
import {loadStripe} from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import IconShield from '@mui/icons-material/Shield';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import useUser from '../../hooks/useUser';
import {useGetCardsQuery, useUpdateCardMutation} from '../../redux/services/paymentApi';

// https://stripe.com/docs/stripe-js/react
const stripePromise = loadStripe(config().stripePublicKey);


const getIssuer = card => {
  return card.brand.toLowerCase().replace(' ', '');
};

const handleSubmit = ({elements, stripe, setStripeError, updateCard, onClose}) => (
  async ({promoCode}) => {
    if (!stripe || !elements) {
      return setStripeError({message: 'Stripe is not ready yet.'});
    }
    const cardElement = elements.getElement("card");
    const {token, error} = await stripe.createToken(cardElement);
    if (error) return setStripeError({message: 'Error - we are unable to connect your credit card. Please make sure all information is accurate. If you continue to have difficulty, please contact us at 202-681-1474 and our Customer Success team will be of assistance!'});
    const response = await updateCard({stripe_token: token.id});
    if (response.error) {
      return setStripeError({message: 'Error updating information'});
    }
    onClose();
  }
);


// Stripe test numbers:
// 4242 4242 4242 4242 Visa
// 5555 5555 5555 4444 MasterCard
// 3782 8224 6310 005 American Express
// 6011 1111 1111 1117 Discover
const CardEditForm = props => {
  const {user} = props;
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const [updateCard] = useUpdateCardMutation();
  return (
    <>
      <DialogContent>
        <Stack spacing={2}>
          {
            stripeError
            ? <Alert severity="error">
              <AlertTitle>Error</AlertTitle>
              {stripeError.message}
            </Alert>
            : <></>
          }
          <CardElement
            onReady={() => setReady(true)}
          />
          <Stack direction='row' spacing={0.5} alignItems='center' style={{paddingTop: '2em'}}>
            <IconShield color='primary' />
            <Typography variant='body' color='grey' style={{fontSize: '0.8rem'}}>
              MovementX does not store your credit card information. Payments processed securely by Stripe.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} variant='outlined'>
          Close
        </Button>
        <Button
            variant='contained'
          onClick={handleSubmit({
            elements,
            stripe,
            setStripeError,
            updateCard,
            onClose: props.onClose,
          })}
        >
          Save
        </Button>
      </DialogActions>
    </>
  );
};

const EditCreditCard = props => {
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const user = useUser();
  const patient = user?.patient || null;
  const response = useGetCardsQuery({}, {skip: false});
  const card = get(response, 'data.data[0]', null);
  const noPaymentInfo = (response?.error?.data?.errors || []).includes('User does not have a customer_id');
  return (
    <Stack spacing={1} padding={1.5} alignItems='center'>
      <Typography variant='h2' padding={2}>Payment Method</Typography>
      {
        patient && card
        ? <Cards
          expiry={(card.exp || '').replace(/[\s]/g, '')}
          preview
          number={'**** **** **** ' + card.digits}
          issuer={getIssuer(card)}
          name={(patient?.user?.first_name || '') + ' ' + (patient?.user?.last_name || '')}
        />
        : null
      }
      <Box>
        <Button
          disabled={!patient}
          onClick={() => setDialogIsOpen(true)}
        >
          Edit Payment Method
        </Button>
      </Box>
      <Dialog
        open={dialogIsOpen}
        fullWidth={true}
        maxWidth='xs'
        onClose={() => setDialogIsOpen(false)}
      >
        <Stack padding={1.5} spacing={1}>
          <Typography variant='h2' align='center'>
            Edit Card Information
          </Typography>
          <Divider/>
          <Elements
            stripe={stripePromise}
            options={{clientSecret: props.stripeClientSecret}}
          >
            <CardEditForm
              user={patient}
              onClose={() => setDialogIsOpen(false)}
            />
          </Elements>
        </Stack>
      </Dialog>
    </Stack>
  );
};

export default EditCreditCard;
